// useAppendDrag — the tail-APPEND gesture for a flow step (P2 #20). BPMN.io's
// append affordance: press the step's tail NUB and drag away to spawn the NEXT
// step connected after it. The OUTCOME equals the connector/context-pad "+"
// (`addStepAfter`), but the GESTURE is a drag-from-tail with a live ghost.
//
// A self-contained pointer session (no view geometry needed — the nub is the
// grip): press arms it; crossing `APPEND_THRESHOLD_PX` promotes to `dragging`
// (which drives the live ghost / connector hint); the release — whether a real
// drag OR a tiny sub-threshold tap — OPENS the add-after input. Committing that
// input funnels through `addStepAfter(stepNodeId, text)` (text stays truth);
// Escape at any point abandons without opening. The whole thing is view state —
// the model only changes on the explicit input commit — so it's unit-testable
// with synthetic MouseEvent-typed pointer events (jsdom lacks PointerEvent).

import { useCallback, useEffect, useRef, useState } from "react";

/** A press event on the nub — carries both axes (the promote threshold is 2-D).
 *  A React or DOM MouseEvent satisfies it; tests pass a bare stub. */
export interface AppendDownEvent {
  readonly clientX: number;
  readonly clientY: number;
  preventDefault(): void;
  stopPropagation(): void;
}

/** Pointer travel (px, either axis) that promotes a nub press into a visible
 *  drag (the ghost appears). Below it the press is still a valid append — the
 *  release opens the input either way — it just never showed the ghost. Matches
 *  the OS slop used by the reorder drag (`DRAG_THRESHOLD_PX`). */
export const APPEND_THRESHOLD_PX = 4;

export type AppendPhase =
  /** No append gesture in flight. */
  | { readonly kind: "idle" }
  /** Pressed the nub; below the threshold — the ghost is not shown yet. */
  | { readonly kind: "arming" }
  /** Past the threshold — dragging away; drives the live "+ new step" ghost. */
  | { readonly kind: "dragging" }
  /** Released — the add-after input is open, awaiting a commit / cancel. */
  | { readonly kind: "adding" };

export interface AppendDragApi {
  readonly phase: AppendPhase;
  /** True while the gesture is live (press → release), for dimming/affordances. */
  readonly active: boolean;
  /** True once past the threshold — show the live ghost / connector hint. */
  readonly dragging: boolean;
  /** True while the add-after input should be open. */
  readonly adding: boolean;
  /** Begin an append gesture from the tail nub. */
  readonly onAppendDown: (e: AppendDownEvent) => void;
  /** Dismiss the add-after input (commit-done / cancel / Escape). */
  readonly closeAdd: () => void;
}

/** The tail-append pointer session. Self-contained: the caller renders the nub +
 *  (when `adding`) the add-after input, and calls `addStepAfter` from that
 *  input's commit — this hook only tracks the gesture lifecycle. */
export function useAppendDrag(): AppendDragApi {
  const [phase, setPhase] = useState<AppendPhase>({ kind: "idle" });
  // Read the live phase inside the window listeners without re-subscribing.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  // The in-flight arm/drag teardown, so an UNMOUNT mid-gesture (e.g. navigating
  // away while pressing) still removes the window listeners — otherwise they
  // leak with a stale closure. Null when no gesture is armed.
  const teardownRef = useRef<(() => void) | null>(null);
  useEffect(() => () => teardownRef.current?.(), []);

  const closeAdd = useCallback(() => setPhase({ kind: "idle" }), []);

  const onAppendDown = useCallback((e: AppendDownEvent) => {
    // Own the gesture: no reorder-body drag, no cell select fires from the nub.
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    setPhase({ kind: "arming" });
    phaseRef.current = { kind: "arming" };

    const teardown = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("keydown", onKey);
      teardownRef.current = null;
    };
    const onMove = (ev: Event) => {
      if (phaseRef.current.kind !== "arming" && phaseRef.current.kind !== "dragging") return;
      const m = ev as MouseEvent;
      if (
        phaseRef.current.kind === "arming" &&
        (Math.abs(m.clientX - startX) > APPEND_THRESHOLD_PX || Math.abs(m.clientY - startY) > APPEND_THRESHOLD_PX)
      ) {
        setPhase({ kind: "dragging" });
        phaseRef.current = { kind: "dragging" };
      }
    };
    const onUp = () => {
      teardown();
      // A completed press — real drag OR a sub-threshold tap — opens the input.
      // (An Escape mid-gesture already tore down + went idle, so we never reach
      // here armed after a cancel.)
      setPhase({ kind: "adding" });
      phaseRef.current = { kind: "adding" };
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        teardown();
        setPhase({ kind: "idle" });
        phaseRef.current = { kind: "idle" };
      }
    };
    teardownRef.current = teardown; // so an unmount mid-gesture can tear down
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("keydown", onKey);
  }, []);

  // While the add-after input is open, Escape closes it (mirrors the reorder
  // drag's Escape-cancel + the inline-edit cancels). The input itself commits on
  // Enter; this is the outer safety net + closes an abandoned open input.
  useEffect(() => {
    if (phase.kind !== "adding") return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setPhase({ kind: "idle" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase.kind]);

  return {
    phase,
    active: phase.kind !== "idle",
    dragging: phase.kind === "dragging",
    adding: phase.kind === "adding",
    onAppendDown,
    closeAdd,
  };
}
